export type PaymentStatusEnum = "pending" | "success" | "failure";

export interface PromoDetailsInterface {
  promoCode?: string;
  actualDiscountValue: number;
}

export interface GstDetailsInterface {
  ratePercentage: number;
  taxableAmount: number;
  actualGstAmount: number;
}

export interface PlatformFeeInterface {
  percentage: number;
  maxCap?: number;
  actualFeeCharged: number;
}

export interface TcsDetailsInterface {
  ratePercentage: number;
  taxableAmount: number;
  actualTcsAmount: number;
}

export interface PaymentDetailsInterface {
  paymentId?: string | null;
  method?: string;
  status: PaymentStatusEnum;
  paidAt?: Date | null;
}

export interface BuyerTransactionFinancialsInterface {
  platformFee: PlatformFeeInterface;
  gstDetails: GstDetailsInterface;
  totalAmountPayable: number;
}

export interface SellerTransactionFinancialsInterface {
  platformFee: PlatformFeeInterface;
  gstDetails: GstDetailsInterface;
  tcsDetails?: TcsDetailsInterface;
  totalAmountPayable: number; // what platform pays to seller
}

export type TransactionType = "PRIMARY" | "REVISION";

export interface OrderTransactionInterface {
  transactionId: string;
  transactionType: TransactionType;
  basePrice: number;
  promoDetails?: PromoDetailsInterface;
  paymentDetails?: PaymentDetailsInterface;
  amountAfterPromo: number;
  buyerFinancials: BuyerTransactionFinancialsInterface;
  sellerFinancials: SellerTransactionFinancialsInterface;
  platformEarnings: number;
}

export interface OrderTotalsInterface {
  totalAmountPayableByBuyer: number;
  totalBuyerGstCollected: number;
  totalSellerGstCollected: number;
  totalTcsCollectedByPlatform: number;
  totalBuyerPlatformFees: number;
  totalSellerPlatformFees: number;
  totalPlatformEarnings: number;
  totalNetAmountReleasedToSeller: number;
}

export interface OrderSummaryInterface {
  orderId: string;
  primaryTransaction: OrderTransactionInterface;
  revisionTransactions: OrderTransactionInterface[];
  releasedPaymentToSeller?: PaymentDetailsInterface;
  orderTotals: OrderTotalsInterface;
  createdAt?: Date;
  updatedAt?: Date;
}

export class PromoDetails implements PromoDetailsInterface {
  promoCode?: string | undefined;
  actualDiscountValue: number;
  constructor(json: any) {
    this.promoCode = json?.promoCode;
    this.actualDiscountValue = Number(json?.actualDiscountValue ?? 0);
  }
}

export class GstDetails implements GstDetailsInterface {
  ratePercentage: number;
  taxableAmount: number;
  actualGstAmount: number;
  constructor(json: any) {
    this.ratePercentage = Number(json?.ratePercentage ?? 0);
    this.taxableAmount = Number(json?.taxableAmount ?? 0);
    this.actualGstAmount = Number(json?.actualGstAmount ?? 0);
  }
}

export class PlatformFee implements PlatformFeeInterface {
  percentage: number;
  maxCap?: number | undefined;
  actualFeeCharged: number;
  constructor(json: any) {
    this.percentage = Number(json?.percentage ?? 0);
    this.maxCap = json?.maxCap != null ? Number(json.maxCap) : undefined;
    this.actualFeeCharged = Number(json?.actualFeeCharged ?? 0);
  }
}

export class TcsDetails implements TcsDetailsInterface {
  ratePercentage: number;
  taxableAmount: number;
  actualTcsAmount: number;
  constructor(json: any) {
    this.ratePercentage = Number(json?.ratePercentage ?? 0);
    this.taxableAmount = Number(json?.taxableAmount ?? 0);
    this.actualTcsAmount = Number(json?.actualTcsAmount ?? 0);
  }
}

export class PaymentDetails implements PaymentDetailsInterface {
  paymentId?: string | null;
  method?: string | undefined;
  status: PaymentStatusEnum;
  paidAt?: Date | null;
  constructor(json: any) {
    this.paymentId = json?.paymentId ?? null;
    this.method = json?.method;
    this.status = (json?.status as PaymentStatusEnum) ?? "pending";
    this.paidAt = json?.paidAt ? new Date(json.paidAt) : null;
  }
}

export class BuyerTransactionFinancials implements BuyerTransactionFinancialsInterface {
  platformFee: PlatformFeeInterface;
  gstDetails: GstDetailsInterface;
  totalAmountPayable: number;
  constructor(json: any) {
    this.platformFee = new PlatformFee(json?.platformFee ?? {});
    this.gstDetails = new GstDetails(json?.gstDetails ?? {});
    this.totalAmountPayable = Number(json?.totalAmountPayable ?? 0);
  }
}

export class SellerTransactionFinancials implements SellerTransactionFinancialsInterface {
  platformFee: PlatformFeeInterface;
  gstDetails: GstDetailsInterface;
  tcsDetails?: TcsDetailsInterface | undefined;
  totalAmountPayable: number;
  constructor(json: any) {
    this.platformFee = new PlatformFee(json?.platformFee ?? {});
    this.gstDetails = new GstDetails(json?.gstDetails ?? {});
    this.tcsDetails = json?.tcsDetails ? new TcsDetails(json.tcsDetails) : undefined;
    this.totalAmountPayable = Number(json?.totalAmountPayable ?? 0);
  }
}

export class OrderTransaction implements OrderTransactionInterface {
  transactionId: string;
  transactionType: TransactionType;
  basePrice: number;
  promoDetails?: PromoDetailsInterface | undefined;
  paymentDetails?: PaymentDetailsInterface | undefined;
  amountAfterPromo: number;
  buyerFinancials: BuyerTransactionFinancialsInterface;
  sellerFinancials: SellerTransactionFinancialsInterface;
  platformEarnings: number;
  constructor(json: any) {
    this.transactionId = json?.transactionId ?? "";
    this.transactionType = (json?.transactionType as TransactionType) ?? "PRIMARY";
    this.basePrice = Number(json?.basePrice ?? 0);
    this.promoDetails = json?.promoDetails ? new PromoDetails(json.promoDetails) : undefined;
    this.paymentDetails = json?.paymentDetails ? new PaymentDetails(json.paymentDetails) : undefined;
    this.amountAfterPromo = Number(json?.amountAfterPromo ?? 0);
    this.buyerFinancials = new BuyerTransactionFinancials(json?.buyerFinancials ?? {});
    this.sellerFinancials = new SellerTransactionFinancials(json?.sellerFinancials ?? {});
    this.platformEarnings = Number(json?.platformEarnings ?? 0);
  }
}

export class OrderTotals implements OrderTotalsInterface {
  totalAmountPayableByBuyer: number;
  totalBuyerGstCollected: number;
  totalSellerGstCollected: number;
  totalTcsCollectedByPlatform: number;
  totalBuyerPlatformFees: number;
  totalSellerPlatformFees: number;
  totalPlatformEarnings: number;
  totalNetAmountReleasedToSeller: number;
  constructor(json: any) {
    this.totalAmountPayableByBuyer = Number(json?.totalAmountPayableByBuyer ?? 0);
    this.totalBuyerGstCollected = Number(json?.totalBuyerGstCollected ?? 0);
    this.totalSellerGstCollected = Number(json?.totalSellerGstCollected ?? 0);
    this.totalTcsCollectedByPlatform = Number(json?.totalTcsCollectedByPlatform ?? 0);
    this.totalBuyerPlatformFees = Number(json?.totalBuyerPlatformFees ?? 0);
    this.totalSellerPlatformFees = Number(json?.totalSellerPlatformFees ?? 0);
    this.totalPlatformEarnings = Number(json?.totalPlatformEarnings ?? 0);
    this.totalNetAmountReleasedToSeller = Number(json?.totalNetAmountReleasedToSeller ?? 0);
  }
}

export class OrderSummary implements OrderSummaryInterface {
  orderId: string;
  primaryTransaction: OrderTransactionInterface;
  revisionTransactions: OrderTransactionInterface[];
  releasedPaymentToSeller?: PaymentDetailsInterface | undefined;
  orderTotals: OrderTotalsInterface;
  createdAt?: Date | undefined;
  updatedAt?: Date | undefined;

  constructor(json: any) {
    this.orderId = json?.orderId ?? json?._id ?? "";
    this.primaryTransaction = new OrderTransaction(json?.primaryTransaction ?? {});
    this.revisionTransactions = Array.isArray(json?.revisionTransactions)
      ? json.revisionTransactions.map((t: any) => new OrderTransaction(t))
      : [];
    this.releasedPaymentToSeller = json?.releasedPaymentToSeller ? new PaymentDetails(json.releasedPaymentToSeller) : undefined;
    this.orderTotals = new OrderTotals(json?.orderTotals ?? {});
    this.createdAt = json?.createdAt ? new Date(json.createdAt) : undefined;
    this.updatedAt = json?.updatedAt ? new Date(json.updatedAt) : undefined;
  }

}


